import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getTheme, themeCssVars, DEFAULT_THEME_ID } from '../../../utils/canvasThemes';
import { shouldRenderOutlineObject } from '../../../utils/allowedOutlineImageSrc';
import { buildTableHtml } from '../../canvas/canvasTableUtils';

/**
 * HtmlSlideRenderer
 *
 * outline 슬라이드 1개를 iframe 안에서 토스 스타일 HTML 슬라이드로 렌더한다.
 *
 * 설계 원칙 (외부 PPT 스킬 22종 조사 결과 반영):
 * - iframe srcDoc으로 CSS 격리 (페이지 전역 스타일 충돌 0건)
 * - Pretendard Variable CDN 강제 (한국어 폰트 항상 일관)
 * - 토큰 시스템 --wqslide-* (lewislulu 패턴)
 * - 라이트 모드 + 큰 글씨 기본 (학교 빔프로젝터 + 수업 영상용)
 * - 16:9 viewport (1280x720), ResizeObserver로 부모 너비에 따라 transform scale
 * - data-layout 락-인: L01-L12 (op7418 패턴) — 이번 PR은 L01-cover만 풀 구현
 * - 한글 자수별 타이틀 사이즈 분기 (op7418 응용)
 * - emoji/이모지 금지 (dokie-ai-ppt 패턴, 토스 스타일 일치)
 *
 * Props:
 *   - slide: outline 슬라이드 1개 객체
 *   - className: 외부 컨테이너 추가 클래스 (선택)
 */

const PRETENDARD_CSS_URL =
  'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css';

const SLIDE_W = 1280;
const SLIDE_H = 720;

// ============================================================
// 폰트 로드 게이트 — FOUT(폴백 글꼴 → Pretendard 교체 깜빡임) 방지
// - Pretendard가 로드되기 전까지 body를 투명 처리 (배경색은 html 캔버스
//   페인팅으로 유지되므로 테마색은 계속 보임 → "깨진 글꼴"이 아예 안 보임)
// - 사용 weight를 명시적으로 load()해서 로드 완료 시점에 정확히 표시
// - CDN 실패/지연 시 타임아웃 안전망으로 무조건 표시 (콘텐츠 유실 0)
// ============================================================
const withCjkFontFallback = (family) => {
  // style="..." 안에 큰따옴표가 들어가면 속성이 끊겨 font-size 등이 전부 사라진다.
  const raw = String(family || 'Pretendard, system-ui, sans-serif').trim().replace(/"/g, "'");
  if (/Noto Sans|Apple SD Gothic|Malgun Gothic/i.test(raw)) return raw;
  return `${raw}, 'Noto Sans CJK KR', 'Noto Sans KR', 'Apple SD Gothic Neo'`;
};

const FONT_GATE_HEAD = `<link rel="preconnect" href="https://cdn.jsdelivr.net" crossorigin />
<link id="wqfont-css" rel="stylesheet" href="${PRETENDARD_CSS_URL}" />
<style>
body { opacity: 0; }
html.wqfonts-ready body { opacity: 1; transition: opacity 0.12s ease; }
</style>`;

const FONT_GATE_SCRIPT = `<script>
(function () {
  var done = false;
  function ready() {
    if (done) return;
    done = true;
    document.documentElement.classList.add('wqfonts-ready');
  }
  function fontsGo() {
    try {
      if (document.fonts && document.fonts.load) {
        var weights = ['400', '500', '600', '700', '800'];
        Promise.all(weights.map(function (w) {
          return document.fonts.load(w + ' 16px Pretendard');
        })).then(ready, ready);
      } else {
        ready();
      }
    } catch (e) { ready(); }
    setTimeout(ready, 900);
  }
  var link = document.getElementById('wqfont-css');
  if (!link || link.sheet) fontsGo();
  else {
    link.addEventListener('load', fontsGo);
    link.addEventListener('error', ready);
    setTimeout(fontsGo, 500);
  }
  setTimeout(ready, 1800);
})();
</script>`;

const titleLenClass = (title) => {
  if (!title) return 'short';
  const len = String(title).length;
  if (len <= 12) return 'short';
  if (len <= 22) return 'medium';
  if (len <= 36) return 'long';
  return 'xlong';
};

const mapLayoutId = (layout) => {
  switch (layout) {
    case 'title_slide':
      return 'L01-cover';
    case 'stat_cards':
      return 'L02-stat-cards';
    case 'section_header':
      return 'L03-section';
    case 'content':
      return 'L04-content';
    case 'content_with_diagram':
      return 'L05-diagram';
    case 'two_column':
      return 'L06-two-column';
    case 'table_compare':
      return 'L07-table-compare';
    case 'quiz':
      return 'L08-quiz';
    case 'timeline':
      return 'L09-timeline';
    case 'callout':
      return 'L11-callout';
    case 'chart':
      return 'L10-chart';
    case 'summary':
      return 'L12-closing';
    default:
      return 'L04-content';
  }
};

export const resolveLayoutVariant = (slide) => {
  const explicit = slide?.layout_variant || slide?.layoutVariant;
  if (explicit) return explicit;

  const layout = slide?.layout || 'content';
  const bullets = (slide?.bullet_points || []).map((item) => String(item || '').trim()).filter(Boolean);
  if (layout === 'content') {
    const longest = bullets.reduce((max, item) => Math.max(max, item.length), 0);
    if (bullets.length >= 1 && bullets.length <= 3) return 'focus_panel';
    if (bullets.length >= 4 && bullets.length <= 6 && longest <= 90) return 'split_cards';
    if (bullets.length >= 4 && bullets.length <= 6) return 'focus_panel';
    return 'numbered_list';
  }
  if (layout === 'section_header') return 'big_number';
  if (layout === 'stat_cards' && (slide?.stat_items || []).length >= 1 && (slide?.stat_items || []).length <= 2) return 'hero_stat';
  if (layout === 'quiz') return 'activity_card';
  if (layout === 'timeline' && (slide?.timeline_items || []).length >= 5) return 'vertical_rail';
  if (layout === 'callout' && (slide?.callout_items || []).length <= 3) return 'stacked_banners';
  if (layout === 'summary') return 'closing_cards';
  return 'default';
};

const visualLabelForSlide = (slide) => {
  const text = [
    slide?.title || '',
    ...(slide?.bullet_points || []).slice(0, 3),
  ].join(' ').toLowerCase();
  const groups = [
    [['광합성', '원자', '분자', '실험', '과학', '에너지', '화학', '물리', '생명'], '과학 개념'],
    [['역사', '왕조', '사건', '전쟁', '시대', '연표', '정변'], '역사 흐름'],
    [['수학', '공식', '계산', '함수', '도형', '확률', '방정식'], '수학 원리'],
    [['기후', '환경', '생태', '지구', '탄소', '날씨'], '지구·환경'],
    [['토론', '질문', '생각', '퀴즈', '활동', '탐구'], '생각 열기'],
    [['핵심', '정리', '요약', '기억'], '핵심 정리'],
  ];
  return groups.find(([keywords]) => keywords.some((keyword) => text.includes(keyword)))?.[1] || '';
};

const escapeHtml = (text) => {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const renderL01Cover = (slide) => {
  const subtitle = slide.subtitle || '';
  const firstBullet = (slide.bullet_points && slide.bullet_points[0]) || '';
  return `
    ${subtitle ? `<div class="wqslide-eyebrow">${escapeHtml(subtitle)}</div>` : ''}
    <h1 class="wqslide-title">${escapeHtml(slide.title || '제목 없음')}</h1>
    ${firstBullet ? `<p class="wqslide-subtitle">${escapeHtml(firstBullet)}</p>` : ''}
    <div class="wqslide-accent-bar"></div>
    <div class="wqslide-footer">왓퀴즈</div>
  `;
};

const renderL02StatCards = (slide) => {
  // stat_items 우선, 없으면 bullet_points 자동 분할 (예: "70%|효율|잎의 활용도")
  const rawItems = slide.stat_items || [];
  let items = rawItems;
  if (items.length === 0 && Array.isArray(slide.bullet_points)) {
    items = slide.bullet_points
      .map((b) => {
        const parts = String(b).split('|').map((p) => p.trim());
        return {
          value: parts[0] || '',
          label: parts[1] || '',
          description: parts[2] || '',
        };
      })
      .filter((it) => it.value);
  }
  items = items.slice(0, 4);
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';

  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--statcards">
      <header class="wqslide-content-header">
        <h2 class="wqslide-content-title">${escapeHtml(slide.title || '핵심 수치')}</h2>
        <span class="wqslide-content-accent-line"></span>
      </header>
      ${
        items.length > 0
          ? `<div class="wqslide-statcards-grid" data-count="${items.length}">${items
              .map(
                (it) => `
            <div class="wqslide-statcard">
              <div class="wqslide-statcard-value">${escapeHtml(it.value)}</div>
              ${it.label ? `<div class="wqslide-statcard-label">${escapeHtml(it.label)}</div>` : ''}
              ${it.description ? `<div class="wqslide-statcard-desc">${escapeHtml(it.description)}</div>` : ''}
            </div>`
              )
              .join('')}</div>`
          : ''
      }
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
  `;
};

const renderL03Section = (slide) => {
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';
  const subtitle = slide.subtitle || (slide.bullet_points && slide.bullet_points[0]) || '';
  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--section">
      ${num ? `<div class="wqslide-section-num">SECTION ${escapeHtml(num)}</div>` : ''}
      <h1 class="wqslide-title">${escapeHtml(slide.title || '섹션 제목')}</h1>
      ${subtitle ? `<p class="wqslide-subtitle">${escapeHtml(subtitle)}</p>` : ''}
      <div class="wqslide-accent-bar"></div>
    </div>
  `;
};

const renderL04Content = (slide) => {
  const bullets = (slide.bullet_points || []).slice(0, 7);
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';
  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--content">
      <header class="wqslide-content-header">
        <h2 class="wqslide-content-title">${escapeHtml(slide.title || '제목')}</h2>
        <span class="wqslide-content-accent-line"></span>
      </header>
      ${
        bullets.length > 0
          ? `<ul class="wqslide-content-bullets">${bullets
              .map(
                (b, i) => `
            <li>
              <span class="wqslide-content-bullet-dot">${i + 1}</span>
              <span class="wqslide-content-bullet-text">${escapeHtml(b)}</span>
            </li>`
              )
              .join('')}</ul>`
          : ''
      }
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
  `;
};

const renderL06TwoColumn = (slide) => {
  // 새 스키마 left_column/right_column 객체 우선, 옛 스키마 left_bullets/left_title fallback
  // (모델이 어떤 형태로 채워도 모두 인식 — 백엔드 빌더와 동기화)
  const bullets = slide.bullet_points || [];
  const lc = slide.left_column || {};
  const rc = slide.right_column || {};
  const left =
    (Array.isArray(lc.bullets) && lc.bullets.length ? lc.bullets : null) ||
    (Array.isArray(lc.items) && lc.items.length ? lc.items : null) ||
    slide.left_bullets ||
    bullets.filter((_, i) => i % 2 === 0);
  const right =
    (Array.isArray(rc.bullets) && rc.bullets.length ? rc.bullets : null) ||
    (Array.isArray(rc.items) && rc.items.length ? rc.items : null) ||
    slide.right_bullets ||
    bullets.filter((_, i) => i % 2 === 1);
  const leftTitle = lc.title || slide.left_title || '핵심 1';
  const rightTitle = rc.title || slide.right_title || '핵심 2';
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';
  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--two-col">
      <header class="wqslide-content-header">
        <h2 class="wqslide-content-title">${escapeHtml(slide.title || '비교')}</h2>
        <span class="wqslide-content-accent-line"></span>
      </header>
      <div class="wqslide-twocol-grid">
        <div class="wqslide-twocol-card">
          <div class="wqslide-twocol-card-title">${escapeHtml(leftTitle)}</div>
          ${
            left.length > 0
              ? `<ul class="wqslide-twocol-bullets">${left
                  .map((b) => `<li>${escapeHtml(b)}</li>`)
                  .join('')}</ul>`
              : ''
          }
        </div>
        <div class="wqslide-twocol-card wqslide-twocol-card--accent">
          <div class="wqslide-twocol-card-title">${escapeHtml(rightTitle)}</div>
          ${
            right.length > 0
              ? `<ul class="wqslide-twocol-bullets">${right
                  .map((b) => `<li>${escapeHtml(b)}</li>`)
                  .join('')}</ul>`
              : ''
          }
        </div>
      </div>
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
  `;
};

const renderL05Diagram = (slide) => {
  // 새 스키마 diagram_steps 우선, 옛 스키마 bullet_points fallback
  // (백엔드 빌더와 동기화)
  let steps;
  if (Array.isArray(slide.diagram_steps) && slide.diagram_steps.length > 0) {
    steps = slide.diagram_steps.slice(0, 5).map((s) => {
      if (typeof s === 'string') return s;
      if (s && typeof s === 'object') {
        const t = s.title || '';
        const d = s.description || '';
        return d ? `${t} (${d})` : t;
      }
      return String(s);
    });
  } else {
    steps = (slide.bullet_points || []).slice(0, 5);
  }
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';
  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--diagram">
      <header class="wqslide-content-header">
        <h2 class="wqslide-content-title">${escapeHtml(slide.title || '단계별 흐름')}</h2>
        <span class="wqslide-content-accent-line"></span>
      </header>
      ${
        steps.length > 0
          ? `<div class="wqslide-diagram-flow">${steps
              .map(
                (s, i) => `
            <div class="wqslide-diagram-step">
              <div class="wqslide-diagram-step-num">${i + 1}</div>
              <div class="wqslide-diagram-step-text">${escapeHtml(s)}</div>
            </div>
            ${
              i < steps.length - 1
                ? `<svg class="wqslide-diagram-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`
                : ''
            }`
              )
              .join('')}</div>`
          : ''
      }
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
  `;
};

const renderL07TableCompare = (slide) => {
  const td = slide.table_data || {};
  const headers = Array.isArray(td.headers) ? td.headers : [];
  const rows = Array.isArray(td.rows) ? td.rows : [];
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';

  // 행/열 수에 따라 셀 사이즈 분기 (한국 교과서 가독성)
  const cellSizeClass =
    rows.length >= 7 ? 'dense' : rows.length >= 5 ? 'medium' : 'roomy';

  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--table">
      <header class="wqslide-content-header">
        <h2 class="wqslide-content-title">${escapeHtml(slide.title || '비교표')}</h2>
        <span class="wqslide-content-accent-line"></span>
      </header>
      ${
        headers.length > 0 || rows.length > 0
          ? `<div class="wqslide-table-wrap" data-density="${cellSizeClass}">
              <table class="wqslide-table">
                ${
                  headers.length > 0
                    ? `<thead><tr>${headers
                        .map((h, i) => `<th data-first="${i === 0}">${escapeHtml(h)}</th>`)
                        .join('')}</tr></thead>`
                    : ''
                }
                <tbody>${rows
                  .map(
                    (row) => `<tr>${(Array.isArray(row) ? row : []).map(
                      (cell, ci) =>
                        `<td data-first-col="${ci === 0}">${escapeHtml(cell)}</td>`
                    ).join('')}</tr>`
                  )
                  .join('')}</tbody>
              </table>
            </div>`
          : ''
      }
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
  `;
};

const renderL08Quiz = (slide) => {
  // 새 스키마 choices/question/answer_idx/explanation 우선, 옛 스키마 bullet_points fallback
  // (백엔드 빌더와 동기화)
  const options =
    (Array.isArray(slide.choices) && slide.choices.length > 0
      ? slide.choices
      : (slide.bullet_points || [])).slice(0, 4).map(String);
  const question = (slide.question || slide.title || '질문').trim();
  // answer_idx: 0-based 또는 1-based 모두 허용
  let answerIdx = null;
  if (typeof slide.answer_idx === 'number') {
    answerIdx = slide.answer_idx;
    if (answerIdx >= options.length) answerIdx = answerIdx - 1;
    if (answerIdx < 0 || answerIdx >= options.length) answerIdx = null;
  }
  const explanation = (slide.explanation || '').trim();
  const circleNum = ['①', '②', '③', '④'];
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';
  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--quiz">
      <div class="wqslide-quiz-eyebrow">
        <span class="wqslide-quiz-q-badge">Q</span>
        <span>퀴즈</span>
      </div>
      <h2 class="wqslide-quiz-question">${escapeHtml(question)}</h2>
      ${
        options.length > 0
          ? `<ol class="wqslide-quiz-options">${options
              .map(
                (o, i) => `
            <li${i === answerIdx ? ' data-correct="true"' : ''}>
              <span class="wqslide-quiz-circle">${circleNum[i] || i + 1}</span>
              <span class="wqslide-quiz-option-text">${escapeHtml(o)}</span>
            </li>`
              )
              .join('')}</ol>`
          : ''
      }
      ${
        explanation
          ? `<div class="wqslide-quiz-explanation">해설 · ${escapeHtml(explanation)}</div>`
          : ''
      }
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
  `;
};

const renderL09Timeline = (slide) => {
  // timeline_items 우선, 없으면 bullet_points 자동 분할 ("1592.4|전쟁 발발|부산 침입")
  let items = Array.isArray(slide.timeline_items) ? slide.timeline_items : [];
  if (items.length === 0 && Array.isArray(slide.bullet_points)) {
    items = slide.bullet_points
      .map((b) => {
        const parts = String(b).split('|').map((p) => p.trim());
        return {
          date: parts[0] || '',
          title: parts[1] || '',
          description: parts[2] || '',
        };
      })
      .filter((it) => it.date || it.title);
  }
  items = items.slice(0, 6);
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';

  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--timeline">
      <header class="wqslide-content-header">
        <h2 class="wqslide-content-title">${escapeHtml(slide.title || '연대표')}</h2>
        <span class="wqslide-content-accent-line"></span>
      </header>
      ${
        items.length > 0
          ? `<div class="wqslide-timeline-track" data-count="${items.length}">
              <div class="wqslide-timeline-line"></div>
              ${items
                .map(
                  (it, i) => `
                <div class="wqslide-timeline-node">
                  <div class="wqslide-timeline-date">${escapeHtml(it.date || '')}</div>
                  <div class="wqslide-timeline-dot">${i + 1}</div>
                  <div class="wqslide-timeline-body">
                    <div class="wqslide-timeline-event">${escapeHtml(it.title || '')}</div>
                    ${
                      it.description
                        ? `<div class="wqslide-timeline-desc">${escapeHtml(it.description)}</div>`
                        : ''
                    }
                  </div>
                </div>`
                )
                .join('')}
            </div>`
          : ''
      }
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
  `;
};

const CALLOUT_LABELS = {
  key: '핵심',
  tip: '참고',
  warn: '주의',
  correct: '정답',
};

const CHART_TYPE_MAP = {
  bar: 'bar',
  column: 'bar', // 한국식 column → Chart.js bar
  line: 'line',
  pie: 'pie',
  doughnut: 'doughnut',
};

const renderL10Chart = (slide) => {
  const cd = slide.chart_data || {};
  const chartType = CHART_TYPE_MAP[String(cd.type || '').toLowerCase()] || 'bar';
  const labels = Array.isArray(cd.labels) ? cd.labels : [];
  const datasets = Array.isArray(cd.datasets) ? cd.datasets : [];
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';

  // 토스 디자인 색상 팔레트 (기본 1색 + 보조 4색)
  const basePalette = ['#3182F6', '#0891B2', '#059669', '#D97706', '#7C3AED'];

  // Chart.js datasets 직렬화 (XSS 방지 위해 JSON.stringify)
  const chartConfig = {
    type: chartType,
    data: {
      labels,
      datasets: datasets.map((ds, i) => ({
        label: ds.label || `시리즈 ${i + 1}`,
        data: Array.isArray(ds.values) ? ds.values : [],
        backgroundColor:
          chartType === 'pie' || chartType === 'doughnut'
            ? basePalette
            : basePalette[i % basePalette.length],
        borderColor:
          chartType === 'line' ? basePalette[i % basePalette.length] : 'transparent',
        borderWidth: chartType === 'line' ? 3 : 0,
        tension: chartType === 'line' ? 0.3 : 0,
        fill: chartType === 'line' ? false : true,
      })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: datasets.length > 1 || chartType === 'pie' || chartType === 'doughnut',
          position: 'top',
          labels: {
            font: { family: 'Pretendard, sans-serif', size: 14, weight: '600' },
            color: '#4E5968',
          },
        },
        tooltip: { enabled: false },
      },
      scales:
        chartType === 'pie' || chartType === 'doughnut'
          ? {}
          : {
              x: {
                title: {
                  display: !!cd.x_axis_label,
                  text: cd.x_axis_label || '',
                  font: { family: 'Pretendard, sans-serif', size: 14, weight: '600' },
                  color: '#4E5968',
                },
                ticks: {
                  font: { family: 'Pretendard, sans-serif', size: 14 },
                  color: '#4E5968',
                },
                grid: { color: '#F2F4F6' },
              },
              y: {
                title: {
                  display: !!cd.y_axis_label,
                  text: cd.y_axis_label || '',
                  font: { family: 'Pretendard, sans-serif', size: 14, weight: '600' },
                  color: '#4E5968',
                },
                ticks: {
                  font: { family: 'Pretendard, sans-serif', size: 14 },
                  color: '#4E5968',
                },
                grid: { color: '#F2F4F6' },
              },
            },
    },
  };

  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--chart">
      <header class="wqslide-content-header">
        <h2 class="wqslide-content-title">${escapeHtml(slide.title || '데이터 차트')}</h2>
        <span class="wqslide-content-accent-line"></span>
      </header>
      ${
        labels.length > 0
          ? `<div class="wqslide-chart-container">
              <canvas id="wqChart" width="1100" height="400"></canvas>
            </div>`
          : '<div class="wqslide-chart-placeholder">차트 데이터 없음</div>'
      }
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
    <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>
    <script>
      window.addEventListener('load', function() {
        try {
          const ctx = document.getElementById('wqChart');
          if (ctx && window.Chart) {
            new window.Chart(ctx, ${JSON.stringify(chartConfig)});
          }
        } catch (e) { console.warn('chart render fail', e); }
      });
    </script>
  `;
};

const renderL11Callout = (slide) => {
  let items = Array.isArray(slide.callout_items) ? slide.callout_items : [];
  if (items.length === 0 && Array.isArray(slide.bullet_points)) {
    items = slide.bullet_points
      .map((b) => {
        const parts = String(b).split('|').map((p) => p.trim());
        if (parts.length >= 2) {
          const maybeVariant = parts[0].toLowerCase();
          if (CALLOUT_LABELS[maybeVariant]) {
            return {
              variant: maybeVariant,
              title: parts[1] || '',
              body: parts[2] || '',
            };
          }
          return {
            variant: 'key',
            title: parts[0] || '',
            body: parts[1] || '',
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  items = items.slice(0, 3);
  const num = slide.slide_number ? String(slide.slide_number).padStart(2, '0') : '';

  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--callout">
      <header class="wqslide-content-header">
        <h2 class="wqslide-content-title">${escapeHtml(slide.title || '핵심 정리')}</h2>
        <span class="wqslide-content-accent-line"></span>
      </header>
      ${
        items.length > 0
          ? `<div class="wqslide-callout-grid" data-count="${items.length}">${items
              .map((it) => {
                const variant = CALLOUT_LABELS[it.variant] ? it.variant : 'key';
                return `
                  <div class="wqslide-callout" data-variant="${variant}">
                    <span class="wqslide-callout-stripe"></span>
                    <div class="wqslide-callout-body">
                      <span class="wqslide-callout-label">${CALLOUT_LABELS[variant]}</span>
                      ${it.title ? `<div class="wqslide-callout-title">${escapeHtml(it.title)}</div>` : ''}
                      ${it.body ? `<div class="wqslide-callout-text">${escapeHtml(it.body)}</div>` : ''}
                    </div>
                  </div>`;
              })
              .join('')}</div>`
          : ''
      }
      ${num ? `<div class="wqslide-page-no">${escapeHtml(num)}</div>` : ''}
    </div>
  `;
};

const renderL12Closing = (slide) => {
  const bullets = (slide.bullet_points || []).slice(0, 5);
  return `
    <div class="wqslide-stage-inner wqslide-stage-inner--closing">
      <div class="wqslide-closing-eyebrow">오늘 정리</div>
      <h2 class="wqslide-closing-title">${escapeHtml(slide.title || '오늘 배운 것')}</h2>
      ${
        bullets.length > 0
          ? `<ul class="wqslide-closing-bullets">${bullets
              .map((b) => `<li>${escapeHtml(b)}</li>`)
              .join('')}</ul>`
          : ''
      }
      <div class="wqslide-closing-footer">왓퀴즈 · whatquiz.co.kr</div>
    </div>
  `;
};

const renderFallback = (slide) => {
  const bullets = (slide.bullet_points || []).slice(0, 5);
  return `
    <div class="wqslide-fallback">
      <div class="wqslide-fallback-badge">${escapeHtml(slide.layout || 'content')}</div>
      <h2 class="wqslide-fallback-title">${escapeHtml(slide.title || '제목')}</h2>
      ${
        bullets.length > 0
          ? `<ul class="wqslide-fallback-bullets">${bullets
              .map((b) => `<li>${escapeHtml(b)}</li>`)
              .join('')}</ul>`
          : ''
      }
    </div>
  `;
};

const LAYOUT_RENDERERS = {
  'L01-cover': renderL01Cover,
  'L02-stat-cards': renderL02StatCards,
  'L03-section': renderL03Section,
  'L04-content': renderL04Content,
  'L05-diagram': renderL05Diagram,
  'L06-two-column': renderL06TwoColumn,
  'L07-table-compare': renderL07TableCompare,
  'L08-quiz': renderL08Quiz,
  'L09-timeline': renderL09Timeline,
  'L10-chart': renderL10Chart,
  'L11-callout': renderL11Callout,
  'L12-closing': renderL12Closing,
};

// Stage G: v2 canvas envelope → 절대 좌표 객체로 보기 모드 렌더링
// 사용자가 자유 캔버스에서 편집한 결과 (locked 객체 + 자유 추가 객체 포함)를
// 갤러리/라이브러리/풀스크린에서도 정확히 표시.
export const buildCanvasObjectsHtml = (canvas) => {
  const objects = Array.isArray(canvas.objects) ? canvas.objects : [];
  const sorted = [...objects].sort((a, b) => (a.z || 0) - (b.z || 0));
  return sorted.map((obj) => {
    if (!shouldRenderOutlineObject(obj)) return '';
    const w = obj.w || 0;
    const h = obj.h || 0;
    const baseStyle = [
      'position:absolute',
      'left:0',
      'top:0',
      `width:${w}px`,
      `height:${h}px`,
      `transform: translate(${obj.x || 0}px, ${obj.y || 0}px) rotate(${obj.rotate || 0}deg)`,
      'transform-origin: center center',
      `z-index:${obj.z || 0}`,
      'box-sizing: border-box',
    ];
    const css = (obj.style || {});
    const fontFamily = withCjkFontFallback(css.fontFamily);

    if (obj.type === 'textbox') {
      const fontParts = [
        `font-family:${fontFamily}`,
        css.fontSize ? `font-size:${css.fontSize}px` : '',
        css.fontWeight ? `font-weight:${css.fontWeight}` : '',
        css.color ? `color:${css.color}` : '',
        css.textAlign ? `text-align:${css.textAlign}` : '',
        css.lineHeight ? `line-height:${css.lineHeight}` : '',
        css.background ? `background:${css.background}` : '',
        css.padding != null ? `padding:${typeof css.padding === 'number' ? css.padding + 'px' : css.padding}` : '',
        css.borderRadius != null ? `border-radius:${typeof css.borderRadius === 'number' ? css.borderRadius + 'px' : css.borderRadius}` : '',
        css.borderLeft ? `border-left:${css.borderLeft}` : '',
        css.display ? `display:${css.display}` : '',
        css.letterSpacing ? `letter-spacing:${css.letterSpacing}` : '',
      ].filter(Boolean).join(';');
      const inner = obj.html != null ? obj.html : escapeHtml(obj.text || '');
      return `<div style="${baseStyle.join(';')};${fontParts}">${inner}</div>`;
    }
    if (obj.type === 'shape') {
      const kind = obj.shape || 'rect';
      const fill = css.fill || css.background || '#E5E8EB';
      if (kind === 'triangle') {
        // CSS 삼각형 (border trick) - 별도 처리
        const triStyle = [
          'position:absolute',
          'left:0', 'top:0',
          'width:0', 'height:0',
          `transform: translate(${obj.x || 0}px, ${obj.y || 0}px) rotate(${obj.rotate || 0}deg)`,
          `border-left: ${w / 2}px solid transparent`,
          `border-right: ${w / 2}px solid transparent`,
          `border-bottom: ${h}px solid ${fill}`,
          `z-index:${obj.z || 0}`,
        ];
        return `<div style="${triStyle.join(';')}"></div>`;
      }
      const shapeParts = [
        `background:${fill}`,
        css.stroke && css.stroke !== 'none' ? `border: ${css.strokeWidth || 2}px solid ${css.stroke}` : '',
        kind === 'circle' || kind === 'ellipse'
          ? 'border-radius: 50%'
          : (css.borderRadius != null ? `border-radius: ${typeof css.borderRadius === 'number' ? css.borderRadius + 'px' : css.borderRadius}` : ''),
      ].filter(Boolean).join(';');
      return `<div style="${baseStyle.join(';')};${shapeParts}"></div>`;
    }
    if (obj.type === 'image' && obj.src) {
      const imgParts = [
        `object-fit:${css.objectFit || 'cover'}`,
        css.borderRadius != null ? `border-radius:${typeof css.borderRadius === 'number' ? css.borderRadius + 'px' : css.borderRadius}` : '',
      ].filter(Boolean).join(';');
      return `<img src="${escapeAttr(obj.src)}" alt="" draggable="false" style="${baseStyle.join(';')};${imgParts}" />`;
    }
    if (obj.type === 'icon' && obj.html) {
      return `<div style="${baseStyle.join(';')}">${obj.html}</div>`;
    }
    if (obj.type === 'table') {
      // WP-F: 편집기(CanvasObject)·백엔드 썸네일(build_table_object_html)과 같은 마크업
      return buildTableHtml(obj, baseStyle.join(';'), fontFamily);
    }
    if (obj.type === 'chart') {
      // canvas 안의 chart 객체는 semantic.chartData에 chart 데이터 보존 (outline_to_canvas가 mapping)
      // Chart.js inline 스크립트로 렌더 (L10-chart와 동일 CDN, sandbox allow-scripts 필요)
      const cd = (obj.semantic && obj.semantic.chartData) || {};
      return buildCanvasChartObjectHtml(obj, baseStyle, cd);
    }
    return '';
  }).join('');
};

// 캔버스의 chart 객체 → Chart.js inline 렌더 (iframe srcDoc 내부에서 실행됨)
const buildCanvasChartObjectHtml = (obj, baseStyle, cd) => {
  const chartTypeKey = String(cd.type || 'bar').toLowerCase();
  const chartTypeMap = { bar: 'bar', column: 'bar', line: 'line', pie: 'pie', doughnut: 'doughnut' };
  const chartType = chartTypeMap[chartTypeKey] || 'bar';
  const labels = Array.isArray(cd.labels) ? cd.labels : [];
  const datasets = Array.isArray(cd.datasets) ? cd.datasets : [];
  const basePalette = ['#3182F6', '#0891B2', '#059669', '#D97706', '#7C3AED'];
  const MUTED = '#C7D2E0';
  const isCircular = chartType === 'pie' || chartType === 'doughnut';
  const singleSeries = datasets.length === 1 && !isCircular;

  // 막대를 전부 같은 색으로 칠하면 어디를 봐야 할지 알 수 없다.
  // 한 계열만 그릴 때는 가장 큰 값 하나만 강조하고 나머지는 무채색으로 물린다.
  const emphasisIndex = singleSeries
    ? (Array.isArray(datasets[0].values) ? datasets[0].values : [])
      .reduce((best, v, i, arr) => (Number(v) > Number(arr[best] ?? -Infinity) ? i : best), 0)
    : -1;

  const chartConfig = {
    type: chartType,
    data: {
      labels,
      datasets: datasets.map((ds, i) => {
        const values = Array.isArray(ds.values) ? ds.values : [];
        let background;
        if (isCircular) {
          background = basePalette;
        } else if (singleSeries) {
          background = values.map((_, vi) => (vi === emphasisIndex ? basePalette[0] : MUTED));
        } else {
          background = basePalette[i % basePalette.length];
        }
        return {
          label: ds.label || `시리즈 ${i + 1}`,
          data: values,
          backgroundColor: background,
          borderColor: chartType === 'line' ? basePalette[i % basePalette.length] : 'transparent',
          borderWidth: chartType === 'line' ? 3 : 0,
          tension: chartType === 'line' ? 0.3 : 0,
          fill: chartType === 'line' ? false : true,
        };
      }),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      // 막대와 간격을 3:2로 두면 값의 크기가 더 또렷하게 읽힌다
      barPercentage: 0.75,
      categoryPercentage: 0.8,
      layout: { padding: { top: 24 } },
      plugins: {
        // 계열이 하나면 범례가 알려주는 정보가 없다. 값을 막대 위에 직접 적는다.
        legend: {
          display: datasets.length > 1 || isCircular,
          position: 'top',
          labels: { font: { family: 'Pretendard, sans-serif', size: 14, weight: '600' }, color: '#4E5968' },
        },
        tooltip: { enabled: false },
      },
      scales: isCircular
        ? {}
        : {
          x: {
            title: { display: !!cd.x_axis_label, text: cd.x_axis_label || '', font: { family: 'Pretendard, sans-serif', size: 14, weight: '600' }, color: '#4E5968' },
            ticks: { font: { family: 'Pretendard, sans-serif', size: 15 }, color: '#4E5968' },
            // 세로 격자선은 값을 읽는 데 도움이 되지 않는다
            grid: { display: false },
            border: { color: '#D1D6DB' },
          },
          y: {
            // 0에서 시작하지 않으면 막대 길이가 값의 비율을 왜곡한다
            beginAtZero: true,
            title: { display: !!cd.y_axis_label, text: cd.y_axis_label || '', font: { family: 'Pretendard, sans-serif', size: 14, weight: '600' }, color: '#4E5968' },
            ticks: { font: { family: 'Pretendard, sans-serif', size: 14 }, color: '#8B95A1', maxTicksLimit: 4 },
            grid: { color: '#EEF1F4', drawTicks: false },
            border: { display: false },
          },
        },
    },
  };
  const cfgJson = JSON.stringify(chartConfig).replace(/</g, '\\u003c');
  const canvasId = `wqcanvas-chart-${obj.id}`;
  const showValueLabels = !isCircular;
  return `<div style="${baseStyle.join(';')};background:#FFFFFF;">
    <canvas id="${canvasId}" style="width:100%;height:100%;"></canvas>
    <script>
    (function() {
      var cfg = ${cfgJson};
      var elId = '${canvasId}';
      // 값을 막대 끝에 직접 적어 범례를 오가지 않고 바로 읽게 한다
      if (${showValueLabels ? 'true' : 'false'}) {
        cfg.plugins = [{
          afterDatasetsDraw: function(chart) {
            var ctx = chart.ctx;
            ctx.save();
            ctx.font = '600 15px Pretendard, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            chart.data.datasets.forEach(function(ds, di) {
              var meta = chart.getDatasetMeta(di);
              if (meta.hidden) return;
              meta.data.forEach(function(pt, i) {
                var v = ds.data[i];
                if (v === null || v === undefined || v === '') return;
                ctx.fillStyle = '#191F28';
                ctx.fillText(String(v), pt.x, pt.y - 6);
              });
            });
            ctx.restore();
          }
        }];
      }
      function tryRender() {
        var el = document.getElementById(elId);
        if (!el || typeof Chart === 'undefined') { setTimeout(tryRender, 60); return; }
        try { new Chart(el, cfg); } catch (e) {}
      }
      tryRender();
    })();
    </script>
  </div>`;
};

const escapeAttr = (s) => String(s || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const buildCanvasSlideHtml = (slide, theme = null) => {
  const canvas = slide.canvas;
  const cw = canvas.width || 1280;
  const ch = canvas.height || 720;
  // Phase 1: theme의 bg를 canvas.background보다 우선시 (slide 영속화된 색)
  // canvas.background는 v2 envelope 생성 시 theme.colors.bg로 set됨
  const bg = canvas.background || (theme ? theme.colors.bg : '#FFFFFF');
  const hasChartObject = Array.isArray(canvas.objects) && canvas.objects.some((o) => o && o.type === 'chart');
  const chartCdn = hasChartObject
    ? '<script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"></script>'
    : '';
  // Phase 1: theme CSS 변수를 :root에 주입 (canvas 안 객체는 style 인라인 색이지만,
  // chart iframe 등은 theme 토큰을 사용할 수 있음)
  const rootVars = theme ? `:root { ${themeCssVars(theme)}; }` : '';
  return `<!doctype html>
<html lang="ko">
<head>
<meta charset="utf-8" />
${FONT_GATE_HEAD}
${chartCdn}
<style>
${rootVars}
html, body { margin: 0; padding: 0; height: 100%; background: ${bg}; font-family: ${withCjkFontFallback('Pretendard, system-ui, -apple-system, sans-serif')}; }
.wqcanvas-view-stage { position: relative; width: ${cw}px; height: ${ch}px; background: ${bg}; overflow: hidden; }
.wqcanvas-view-stage *[contenteditable] { outline: none; }
.wqcanvas-view-stage img { max-width: none; }
</style>
</head>
<body>
<div class="wqcanvas-view-stage">
${buildCanvasObjectsHtml(canvas)}
</div>
${FONT_GATE_SCRIPT}
</body>
</html>`;
};

const buildSlideHtml = (slide, theme = null) => {
  // v2 canvas envelope이면 절대 좌표 객체로 렌더
  if (slide && slide.canvas && Array.isArray(slide.canvas.objects)) {
    return buildCanvasSlideHtml(slide, theme);
  }
  const layoutId = mapLayoutId(slide.layout);
  const tLen = titleLenClass(slide.title);
  const variant = resolveLayoutVariant(slide);
  const renderer = LAYOUT_RENDERERS[layoutId] || renderFallback;
  const inner = renderer(slide);
  const visualLabel = !['L01-cover', 'L03-section'].includes(layoutId) && String(slide?.title || '').length <= 32
    ? visualLabelForSlide(slide)
    : '';

  // Phase 1: theme이 있으면 :root 토큰을 그 값으로, 없으면 토스 기본
  const themeVars = theme ? themeCssVars(theme) : null;

  return `<!doctype html>
<html lang="ko">
<head>
<meta charset="utf-8" />
${FONT_GATE_HEAD}
<style>
/* ===== 디자인 토큰 — Phase 1: 9 테마 동적 주입 (테마 없으면 토스 기본) ===== */
:root {
${themeVars ? `  ${themeVars.replace(/; /g, ';\n  ')};` : `  --wqslide-bg: #FFFFFF;
  --wqslide-bg-subtle: #F2F4F6;
  --wqslide-text-1: #191F28;
  --wqslide-text-2: #4E5968;
  --wqslide-text-3: #8B95A1;
  --wqslide-accent: #3182F6;
  --wqslide-accent-soft: #DBEAFE;
  --wqslide-accent-strong: #2563EB;
  --wqslide-border: #E5E8EB;
  --wqslide-success: #059669;
  --wqslide-success-soft: #D1FAE5;
  --wqslide-success-strong: #047857;
  --wqslide-warn: #D97706;
  --wqslide-warn-soft: #FEF3C7;
  --wqslide-warn-strong: #B45309;
  --wqslide-info: #0891B2;
  --wqslide-info-soft: #CFFAFE;
  --wqslide-info-strong: #077491;
  --wqslide-radius: 20px;
  --wqslide-radius-pill: 999px;
  --wqslide-padding: 80px;`}

  --wqslide-font-family: 'Pretendard Variable', 'Pretendard', 'Noto Sans KR', 'Malgun Gothic', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;

  --wqslide-fs-title: 72px;
  --wqslide-fs-subtitle: 36px;
  --wqslide-fs-body: 28px;
  --wqslide-fs-caption: 18px;
}

* { box-sizing: border-box; margin: 0; padding: 0; }

html, body {
  width: ${SLIDE_W}px;
  height: ${SLIDE_H}px;
  overflow: hidden;
  background: var(--wqslide-bg);
  font-family: var(--wqslide-font-family);
  color: var(--wqslide-text-1);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  letter-spacing: -0.02em;
  font-feature-settings: "tnum";
}

.wqslide-stage {
  width: ${SLIDE_W}px;
  height: ${SLIDE_H}px;
  position: relative;
  overflow: hidden;
}

/* ============================================ */
/* L01-cover: 표지 슬라이드 (이번 PR 풀 구현)      */
/* ============================================ */
.wqslide-stage[data-layout="L01-cover"] {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: var(--wqslide-padding);
  gap: 24px;
  background: linear-gradient(180deg, #FFFFFF 0%, #F9FBFE 100%);
}

.wqslide-stage[data-layout="L01-cover"]:not([data-variant="bold_band"])::before {
  content: "";
  position: absolute;
  top: -110px;
  right: -80px;
  width: 330px;
  height: 330px;
  border-radius: 50%;
  background: var(--wqslide-accent-soft);
}

.wqslide-stage[data-layout="L01-cover"]:not([data-variant="bold_band"])::after {
  content: "";
  position: absolute;
  top: 160px;
  right: 78px;
  width: 74px;
  height: 74px;
  border-radius: 50%;
  background: var(--wqslide-accent);
}

.wqslide-stage[data-layout="L01-cover"] .wqslide-eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  background: var(--wqslide-accent-soft);
  color: var(--wqslide-accent);
  border-radius: var(--wqslide-radius-pill);
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.wqslide-stage[data-layout="L01-cover"] .wqslide-title {
  font-size: var(--wqslide-fs-title);
  font-weight: 700;
  line-height: 1.2;
  color: var(--wqslide-text-1);
  max-width: 90%;
  word-break: keep-all;
}

.wqslide-stage[data-layout="L01-cover"] .wqslide-subtitle {
  font-size: var(--wqslide-fs-subtitle);
  font-weight: 500;
  color: var(--wqslide-text-2);
  line-height: 1.4;
  max-width: 85%;
  word-break: keep-all;
}

.wqslide-stage[data-layout="L01-cover"] .wqslide-accent-bar {
  width: 80px;
  height: 6px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 8px;
}

.wqslide-stage[data-layout="L01-cover"] .wqslide-footer {
  position: absolute;
  bottom: 60px;
  left: var(--wqslide-padding);
  font-size: var(--wqslide-fs-caption);
  color: var(--wqslide-text-3);
  letter-spacing: 0.04em;
  font-weight: 500;
}

.wqslide-stage[data-layout="L01-cover"][data-variant="bold_band"] {
  justify-content: center;
  padding-left: 470px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L01-cover"][data-variant="bold_band"]::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 410px;
  background: var(--wqslide-text-1);
}
.wqslide-stage[data-layout="L01-cover"][data-variant="bold_band"]::after {
  content: "";
  position: absolute;
  left: 380px;
  top: 0;
  bottom: 0;
  width: 16px;
  background: var(--wqslide-accent);
}
.wqslide-stage[data-layout="L01-cover"][data-variant="bold_band"] .wqslide-title,
.wqslide-stage[data-layout="L01-cover"][data-variant="bold_band"] .wqslide-subtitle,
.wqslide-stage[data-layout="L01-cover"][data-variant="bold_band"] .wqslide-accent-bar {
  position: relative;
  z-index: 2;
}

/* 한글 자수별 타이틀 사이즈 분기 (op7418 패턴 응용) */
.wqslide-stage[data-title-len="medium"] .wqslide-title { font-size: 60px; }
.wqslide-stage[data-title-len="long"]   .wqslide-title { font-size: 50px; }
.wqslide-stage[data-title-len="xlong"]  .wqslide-title { font-size: 42px; line-height: 1.25; }

/* ============================================ */
/* L02-stat-cards: 통계 카드 (한국 교과서 인포그래픽) */
/* 60-30-10 색상 법칙: 흰 60% + 회색 30% + 파랑 10%   */
/* ============================================ */
.wqslide-stage[data-layout="L02-stat-cards"] {
  padding: 64px 72px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-stage-inner--statcards {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 36px;
}
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-content-title {
  font-size: 50px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.02em;
}
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-content-accent-line {
  display: block;
  width: 64px;
  height: 5px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 12px;
}
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcards-grid {
  flex: 1;
  display: grid;
  gap: 24px;
  align-content: center;
}
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcards-grid[data-count="1"] { grid-template-columns: 1fr; }
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcards-grid[data-count="2"] { grid-template-columns: 1fr 1fr; }
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcards-grid[data-count="3"] { grid-template-columns: 1fr 1fr 1fr; }
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcards-grid[data-count="4"] { grid-template-columns: 1fr 1fr 1fr 1fr; }

.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcard {
  background: var(--wqslide-bg-subtle);
  border: 1px solid var(--wqslide-border);
  border-radius: 16px;
  padding: 36px 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  position: relative;
  overflow: hidden;
}

/* 좌측 파랑 강조 stripe (정보 시각화 계층) */
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcard::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: var(--wqslide-accent);
}

.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcard-value {
  font-size: 64px;
  font-weight: 800;
  line-height: 1.1;
  color: var(--wqslide-text-1);
  letter-spacing: -0.03em;
  font-feature-settings: "tnum";
  word-break: keep-all;
}

/* 4개 카드일 때 자동 축소 */
.wqslide-statcards-grid[data-count="4"] .wqslide-statcard-value { font-size: 52px; }
.wqslide-statcards-grid[data-count="3"] .wqslide-statcard-value { font-size: 60px; }

/* 값이 긴 한글일 때 (예: "C₆H₁₂O₆") */
.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcard-value[data-len="long"] { font-size: 40px; }

.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcard-label {
  font-size: 20px;
  font-weight: 700;
  color: var(--wqslide-accent-strong);
  letter-spacing: -0.01em;
  word-break: keep-all;
}

.wqslide-stage[data-layout="L02-stat-cards"] .wqslide-statcard-desc {
  font-size: 16px;
  font-weight: 500;
  color: var(--wqslide-text-2);
  line-height: 1.5;
  margin-top: 4px;
  word-break: keep-all;
}

.wqslide-stage[data-layout="L02-stat-cards"][data-variant="hero_stat"] .wqslide-statcards-grid {
  grid-template-columns: 1.25fr 0.8fr;
  grid-auto-rows: 1fr;
}

.wqslide-stage[data-layout="L02-stat-cards"][data-variant="hero_stat"] .wqslide-statcard:first-child {
  grid-row: 1 / span 3;
  min-height: 380px;
  background: var(--wqslide-text-1);
  color: #ffffff;
  justify-content: center;
  padding: 46px 42px;
}

.wqslide-stage[data-layout="L02-stat-cards"][data-variant="hero_stat"] .wqslide-statcard:first-child .wqslide-statcard-value {
  font-size: 88px;
  color: #ffffff;
}

.wqslide-stage[data-layout="L02-stat-cards"][data-variant="hero_stat"] .wqslide-statcard:first-child .wqslide-statcard-label,
.wqslide-stage[data-layout="L02-stat-cards"][data-variant="hero_stat"] .wqslide-statcard:first-child .wqslide-statcard-desc {
  color: rgba(255,255,255,0.82);
}

.wqslide-stage[data-layout="L02-stat-cards"][data-variant="hero_stat"] .wqslide-statcard:not(:first-child) {
  min-height: 112px;
  padding: 20px 22px;
}

.wqslide-stage[data-layout="L02-stat-cards"][data-variant="hero_stat"] .wqslide-statcard:not(:first-child) .wqslide-statcard-value {
  font-size: 34px;
}

/* ============================================ */
/* L03-section: 섹션 헤더                       */
/* ============================================ */
.wqslide-stage[data-layout="L03-section"] {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--wqslide-padding);
  background: linear-gradient(135deg, #F3F8FF 0%, #FFFFFF 100%);
}
.wqslide-stage[data-layout="L03-section"] .wqslide-stage-inner--section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 90%;
}
.wqslide-stage[data-layout="L03-section"] .wqslide-section-num {
  font-size: 24px;
  font-weight: 700;
  color: var(--wqslide-accent);
  letter-spacing: 0.15em;
}
.wqslide-stage[data-layout="L03-section"] .wqslide-title {
  font-size: 84px;
  font-weight: 800;
  color: var(--wqslide-text-1);
  line-height: 1.15;
  word-break: keep-all;
  letter-spacing: -0.025em;
}
.wqslide-stage[data-layout="L03-section"] .wqslide-subtitle {
  font-size: 32px;
  font-weight: 500;
  color: var(--wqslide-text-2);
  line-height: 1.4;
  margin-top: 8px;
  word-break: keep-all;
}
.wqslide-stage[data-layout="L03-section"] .wqslide-accent-bar {
  width: 100px;
  height: 8px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 12px;
}

.wqslide-stage[data-layout="L03-section"][data-variant="big_number"] {
  justify-content: flex-start;
  align-items: stretch;
  padding: 110px 80px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L03-section"][data-variant="big_number"] .wqslide-section-num {
  font-size: 180px;
  color: var(--wqslide-accent-soft);
  letter-spacing: -0.06em;
  line-height: 0.9;
}
.wqslide-stage[data-layout="L03-section"][data-variant="big_number"] .wqslide-title {
  font-size: 66px;
  max-width: 760px;
  color: var(--wqslide-text-1);
}

/* ============================================ */
/* L04-content: 본문 (가장 많이 사용)            */
/* ============================================ */
.wqslide-stage[data-layout="L04-content"] {
  padding: 64px 72px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L04-content"] .wqslide-stage-inner--content {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 28px;
}
.wqslide-stage[data-layout="L04-content"] .wqslide-content-header {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.wqslide-stage[data-layout="L04-content"] .wqslide-content-title {
  font-size: 54px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.02em;
}
.wqslide-stage[data-layout="L04-content"] .wqslide-content-accent-line {
  display: block;
  width: 64px;
  height: 5px;
  background: var(--wqslide-accent);
  border-radius: 4px;
}
.wqslide-stage[data-layout="L04-content"] .wqslide-content-bullets {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
}
.wqslide-stage[data-layout="L04-content"] .wqslide-content-bullets li {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  font-size: 28px;
  color: var(--wqslide-text-1);
  font-weight: 500;
  line-height: 1.5;
  word-break: keep-all;
}
.wqslide-stage[data-layout="L04-content"] .wqslide-content-bullet-dot {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--wqslide-accent-soft);
  color: var(--wqslide-accent-strong);
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0;
  margin-top: 6px;
}
.wqslide-stage[data-layout="L04-content"] .wqslide-content-bullet-text {
  flex: 1;
}

/* 본문 항목 많을 때 자동 축소 */
.wqslide-stage[data-layout="L04-content"] .wqslide-content-bullets:has(li:nth-child(6)) li {
  font-size: 24px;
  gap: 14px;
}
.wqslide-stage[data-layout="L04-content"] .wqslide-content-bullets:has(li:nth-child(6)) .wqslide-content-bullet-dot {
  width: 28px;
  height: 28px;
  font-size: 14px;
}

.wqslide-stage[data-layout="L04-content"][data-variant="split_cards"] .wqslide-content-bullets {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.wqslide-stage[data-layout="L04-content"][data-variant="split_cards"] .wqslide-content-bullets li {
  min-height: 118px;
  padding: 20px;
  background: var(--wqslide-bg-subtle);
  border: 1px solid var(--wqslide-border);
  border-radius: var(--wqslide-radius);
}
.wqslide-stage[data-layout="L04-content"][data-variant="split_cards"] .wqslide-content-bullet-dot {
  width: 42px;
  height: 30px;
  border-radius: var(--wqslide-radius-pill);
  margin-top: 0;
}
.wqslide-stage[data-layout="L04-content"][data-variant="split_cards"] .wqslide-content-bullet-text {
  font-size: 22px;
  line-height: 1.42;
}

.wqslide-stage[data-layout="L04-content"][data-variant="focus_panel"] .wqslide-content-bullets {
  display: grid;
  grid-template-columns: 0.92fr 1fr;
  gap: 20px 32px;
}
.wqslide-stage[data-layout="L04-content"][data-variant="focus_panel"] .wqslide-content-bullets li:first-child {
  grid-row: 1 / span 5;
  min-height: 340px;
  padding: 34px;
  flex-direction: column;
  justify-content: center;
  background: var(--wqslide-accent-soft);
  border-radius: var(--wqslide-radius);
}
.wqslide-stage[data-layout="L04-content"][data-variant="focus_panel"] .wqslide-content-bullets li:first-child .wqslide-content-bullet-dot {
  width: auto;
  height: auto;
  padding: 7px 16px;
  border-radius: var(--wqslide-radius-pill);
}
.wqslide-stage[data-layout="L04-content"][data-variant="focus_panel"] .wqslide-content-bullets li:first-child .wqslide-content-bullet-text {
  font-size: 34px;
  font-weight: 800;
  line-height: 1.3;
}

.wqslide-stage[data-layout="L04-content"][data-variant="numbered_list"] .wqslide-content-bullets {
  gap: 8px;
}

.wqslide-stage[data-layout="L04-content"][data-variant="numbered_list"] .wqslide-content-bullets li {
  min-height: 58px;
  padding: 10px 16px;
  background: var(--wqslide-bg-subtle);
  border-radius: 12px;
}

.wqslide-stage[data-has-context="true"] .wqslide-content-header {
  padding-right: 190px;
}

.wqslide-context-label {
  position: absolute;
  top: 68px;
  right: 72px;
  z-index: 8;
  min-width: 150px;
  padding: 9px 16px;
  color: var(--wqslide-accent-strong);
  background: var(--wqslide-accent-soft);
  border-radius: var(--wqslide-radius-pill);
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
}

/* ============================================ */
/* L06-two-column: 2단 비교                     */
/* ============================================ */
.wqslide-stage[data-layout="L06-two-column"] {
  padding: 64px 72px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-stage-inner--two-col {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-content-title {
  font-size: 50px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.02em;
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-content-accent-line {
  display: block;
  width: 64px;
  height: 5px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 12px;
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  min-height: 0;
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-card {
  background: var(--wqslide-bg-subtle);
  border-radius: 16px;
  padding: 36px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  border: 1px solid var(--wqslide-border);
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-card--accent {
  background: linear-gradient(160deg, #EFF6FF 0%, #FAFCFF 100%);
  border-color: var(--wqslide-accent-soft);
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-card-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  letter-spacing: -0.02em;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--wqslide-border);
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-card--accent .wqslide-twocol-card-title {
  color: var(--wqslide-accent-strong);
  border-bottom-color: var(--wqslide-accent);
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-bullets {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-bullets li {
  font-size: 22px;
  color: var(--wqslide-text-2);
  font-weight: 500;
  line-height: 1.5;
  padding-left: 20px;
  position: relative;
  word-break: keep-all;
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-bullets li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 12px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--wqslide-text-3);
}
.wqslide-stage[data-layout="L06-two-column"] .wqslide-twocol-card--accent .wqslide-twocol-bullets li::before {
  background: var(--wqslide-accent);
}

.wqslide-stage[data-layout="L06-two-column"][data-variant="contrast_panels"] .wqslide-twocol-card:first-child {
  background: var(--wqslide-text-1);
}
.wqslide-stage[data-layout="L06-two-column"][data-variant="contrast_panels"] .wqslide-twocol-card:first-child .wqslide-twocol-card-title,
.wqslide-stage[data-layout="L06-two-column"][data-variant="contrast_panels"] .wqslide-twocol-card:first-child .wqslide-twocol-bullets li {
  color: #ffffff;
}
.wqslide-stage[data-layout="L06-two-column"][data-variant="contrast_panels"] .wqslide-twocol-card:first-child .wqslide-twocol-bullets li::before {
  background: #ffffff;
}

/* ============================================ */
/* L07-table-compare: 한국 교과서식 비교표        */
/* 헤더 회색 + 첫 열 강조 + 가는 보더 단정 디자인  */
/* ============================================ */
.wqslide-stage[data-layout="L07-table-compare"] {
  padding: 64px 72px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-stage-inner--table {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 28px;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-content-title {
  font-size: 50px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.02em;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-content-accent-line {
  display: block;
  width: 64px;
  height: 5px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 12px;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-table-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  border: 1px solid var(--wqslide-border);
  border-radius: 12px;
  overflow: hidden;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-table thead th {
  background: var(--wqslide-bg-subtle);
  color: var(--wqslide-text-1);
  font-weight: 700;
  font-size: 22px;
  padding: 18px 20px;
  text-align: left;
  border-bottom: 2px solid var(--wqslide-accent);
  word-break: keep-all;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-table thead th[data-first="true"] {
  background: #EFF6FF;
  color: var(--wqslide-accent-strong);
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-table tbody td {
  padding: 16px 20px;
  font-size: 20px;
  color: var(--wqslide-text-1);
  border-bottom: 1px solid var(--wqslide-border);
  border-right: 1px solid var(--wqslide-border);
  word-break: keep-all;
  vertical-align: middle;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-table tbody td:last-child {
  border-right: none;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-table tbody tr:last-child td {
  border-bottom: none;
}
.wqslide-stage[data-layout="L07-table-compare"] .wqslide-table tbody td[data-first-col="true"] {
  background: var(--wqslide-bg-subtle);
  font-weight: 700;
  color: var(--wqslide-text-1);
}
/* 행 수가 많으면 자동 축소 */
.wqslide-table-wrap[data-density="medium"] .wqslide-table thead th { padding: 14px 18px; font-size: 20px; }
.wqslide-table-wrap[data-density="medium"] .wqslide-table tbody td { padding: 12px 18px; font-size: 18px; }
.wqslide-table-wrap[data-density="dense"] .wqslide-table thead th { padding: 10px 14px; font-size: 17px; }
.wqslide-table-wrap[data-density="dense"] .wqslide-table tbody td { padding: 8px 14px; font-size: 15px; }

/* ============================================ */
/* L05-diagram: 단계 흐름 다이어그램               */
/* ============================================ */
.wqslide-stage[data-layout="L05-diagram"] {
  padding: 64px 56px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-stage-inner--diagram {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 36px;
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-content-title {
  font-size: 50px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.02em;
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-content-accent-line {
  display: block;
  width: 64px;
  height: 5px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 12px;
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-diagram-flow {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-diagram-step {
  flex: 1;
  min-width: 140px;
  max-width: 220px;
  background: var(--wqslide-bg-subtle);
  border: 2px solid var(--wqslide-border);
  border-radius: 16px;
  padding: 24px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-diagram-step-num {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--wqslide-accent);
  color: #FFFFFF;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0;
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-diagram-step-text {
  font-size: 22px;
  font-weight: 600;
  color: var(--wqslide-text-1);
  line-height: 1.4;
  word-break: keep-all;
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-diagram-arrow {
  width: 36px;
  height: 36px;
  color: var(--wqslide-accent);
  flex-shrink: 0;
}

/* 단계 5개일 때 자동 축소 */
.wqslide-stage[data-layout="L05-diagram"] .wqslide-diagram-flow:has(.wqslide-diagram-step:nth-child(9)) .wqslide-diagram-step {
  min-width: 110px;
  padding: 18px 12px;
}
.wqslide-stage[data-layout="L05-diagram"] .wqslide-diagram-flow:has(.wqslide-diagram-step:nth-child(9)) .wqslide-diagram-step-text {
  font-size: 18px;
}

.wqslide-stage[data-layout="L05-diagram"][data-variant="vertical_steps"] .wqslide-diagram-flow {
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
}
.wqslide-stage[data-layout="L05-diagram"][data-variant="vertical_steps"] .wqslide-diagram-step {
  min-width: 100%;
  min-height: 72px;
  flex-direction: row;
  justify-content: flex-start;
  padding: 18px 24px;
}
.wqslide-stage[data-layout="L05-diagram"][data-variant="vertical_steps"] .wqslide-diagram-step-text {
  text-align: left;
  font-size: 22px;
}
.wqslide-stage[data-layout="L05-diagram"][data-variant="vertical_steps"] .wqslide-diagram-arrow {
  display: none;
}

/* ============================================ */
/* L08-quiz: 퀴즈 (4지선다)                      */
/* ============================================ */
.wqslide-stage[data-layout="L08-quiz"] {
  padding: 56px 72px;
  background: linear-gradient(180deg, #FFFFFF 0%, #FFF9E6 100%);
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-stage-inner--quiz {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  align-self: flex-start;
  font-size: 22px;
  font-weight: 700;
  color: #B45309;
  letter-spacing: 0.04em;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-q-badge {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  background: #F59E0B;
  color: #FFFFFF;
  border-radius: 12px;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-question {
  font-size: 48px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.3;
  word-break: keep-all;
  letter-spacing: -0.02em;
  margin-top: 4px;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-options {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-top: 12px;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-options li {
  background: #FFFFFF;
  border: 2px solid #FDE68A;
  border-radius: 14px;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 24px;
  color: var(--wqslide-text-1);
  font-weight: 500;
  line-height: 1.4;
  word-break: keep-all;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-circle {
  flex-shrink: 0;
  font-size: 28px;
  font-weight: 700;
  color: #D97706;
  letter-spacing: 0;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-option-text {
  flex: 1;
}

/* 보기가 1~2개이면 1열 grid */
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-options:has(li:only-child),
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-options:has(li:nth-child(2):last-child) {
  grid-template-columns: 1fr;
}

/* 정답 강조 (data-correct=true) */
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-options li[data-correct="true"] {
  background: #D1FAE5;
  border-color: #059669;
  border-width: 3px;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-options li[data-correct="true"] .wqslide-quiz-circle {
  color: #059669;
}
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-options li[data-correct="true"] .wqslide-quiz-option-text {
  font-weight: 700;
}

/* 해설 영역 */
.wqslide-stage[data-layout="L08-quiz"] .wqslide-quiz-explanation {
  margin-top: 24px;
  padding: 18px 22px;
  background: #FEF3C7;
  border-left: 4px solid #D97706;
  border-radius: 10px;
  font-size: 18px;
  color: var(--wqslide-text-2);
  line-height: 1.55;
  word-break: keep-all;
}

/* ============================================ */
/* L09-timeline: 가로 타임라인 (한국 역사·과학사) */
/* ============================================ */
.wqslide-stage[data-layout="L09-timeline"] {
  padding: 64px 60px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L09-timeline"] .wqslide-stage-inner--timeline {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
}
.wqslide-stage[data-layout="L09-timeline"] .wqslide-content-title {
  font-size: 50px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.02em;
}
.wqslide-stage[data-layout="L09-timeline"] .wqslide-content-accent-line {
  display: block;
  width: 64px;
  height: 5px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 12px;
}

.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-track {
  flex: 1;
  position: relative;
  display: grid;
  align-items: center;
  padding: 60px 20px;
}
.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-track[data-count="3"] { grid-template-columns: repeat(3, 1fr); }
.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-track[data-count="4"] { grid-template-columns: repeat(4, 1fr); }
.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-track[data-count="5"] { grid-template-columns: repeat(5, 1fr); }
.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-track[data-count="6"] { grid-template-columns: repeat(6, 1fr); }

/* 타임라인 수평선 (점 사이를 잇는 회색 라인) */
.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-line {
  position: absolute;
  top: 50%;
  left: 8%;
  right: 8%;
  height: 3px;
  background: var(--wqslide-border);
  transform: translateY(-50%);
  z-index: 0;
}

.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-node {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
}

.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-date {
  font-size: 18px;
  font-weight: 700;
  color: var(--wqslide-accent-strong);
  letter-spacing: 0.02em;
  margin-bottom: 16px;
  font-feature-settings: "tnum";
  text-align: center;
  word-break: keep-all;
  min-height: 30px;
}

.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-dot {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--wqslide-accent);
  color: #FFFFFF;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0;
  border: 4px solid #FFFFFF;
  box-shadow: 0 0 0 2px var(--wqslide-accent);
  flex-shrink: 0;
}

.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-body {
  margin-top: 18px;
  text-align: center;
  max-width: 200px;
}

.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-event {
  font-size: 18px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.35;
  word-break: keep-all;
}

.wqslide-stage[data-layout="L09-timeline"] .wqslide-timeline-desc {
  font-size: 14px;
  font-weight: 500;
  color: var(--wqslide-text-2);
  line-height: 1.45;
  margin-top: 6px;
  word-break: keep-all;
}

/* 6개 노드면 텍스트 축소 */
.wqslide-timeline-track[data-count="6"] .wqslide-timeline-event { font-size: 15px; }
.wqslide-timeline-track[data-count="6"] .wqslide-timeline-desc { font-size: 12px; }
.wqslide-timeline-track[data-count="6"] .wqslide-timeline-body { max-width: 160px; }

.wqslide-stage[data-layout="L09-timeline"][data-variant="vertical_rail"] .wqslide-timeline-track {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-left: 52px;
}
.wqslide-stage[data-layout="L09-timeline"][data-variant="vertical_rail"] .wqslide-timeline-line {
  left: 22px;
  top: 12px;
  bottom: 12px;
  width: 3px;
  height: auto;
}
.wqslide-stage[data-layout="L09-timeline"][data-variant="vertical_rail"] .wqslide-timeline-node {
  display: grid;
  grid-template-columns: 110px 34px 1fr;
  gap: 16px;
  align-items: start;
}
.wqslide-stage[data-layout="L09-timeline"][data-variant="vertical_rail"] .wqslide-timeline-date,
.wqslide-stage[data-layout="L09-timeline"][data-variant="vertical_rail"] .wqslide-timeline-body {
  text-align: left;
}

/* ============================================ */
/* L10-chart: Chart.js 데이터 차트                 */
/* ============================================ */
.wqslide-stage[data-layout="L10-chart"] {
  padding: 56px 72px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L10-chart"] .wqslide-stage-inner--chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.wqslide-stage[data-layout="L10-chart"] .wqslide-content-title {
  font-size: 44px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.02em;
}
.wqslide-stage[data-layout="L10-chart"] .wqslide-content-accent-line {
  display: block;
  width: 64px;
  height: 5px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 12px;
}
.wqslide-stage[data-layout="L10-chart"] .wqslide-chart-container {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--wqslide-bg);
  border-radius: 12px;
  padding: 16px;
  min-height: 0;
}
.wqslide-stage[data-layout="L10-chart"] .wqslide-chart-container canvas {
  width: 100% !important;
  height: 100% !important;
  max-height: 460px;
}
.wqslide-stage[data-layout="L10-chart"] .wqslide-chart-placeholder {
  font-size: 24px;
  color: var(--wqslide-text-3);
  text-align: center;
  padding: 60px;
}

/* ============================================ */
/* L11-callout: 강조 박스 (variant 4종)            */
/* ============================================ */
.wqslide-stage[data-layout="L11-callout"] {
  padding: 64px 72px;
  background: var(--wqslide-bg);
}
.wqslide-stage[data-layout="L11-callout"] .wqslide-stage-inner--callout {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
}
.wqslide-stage[data-layout="L11-callout"] .wqslide-content-title {
  font-size: 50px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.02em;
}
.wqslide-stage[data-layout="L11-callout"] .wqslide-content-accent-line {
  display: block;
  width: 64px;
  height: 5px;
  background: var(--wqslide-accent);
  border-radius: 4px;
  margin-top: 12px;
}
.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-grid {
  flex: 1;
  display: grid;
  gap: 24px;
  align-content: center;
}
.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-grid[data-count="1"] { grid-template-columns: 1fr; }
.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-grid[data-count="2"] { grid-template-columns: 1fr 1fr; }
.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-grid[data-count="3"] { grid-template-columns: 1fr 1fr 1fr; }

.wqslide-stage[data-layout="L11-callout"] .wqslide-callout {
  position: relative;
  display: flex;
  background: var(--wqslide-accent-soft);
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid transparent;
}

.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-stripe {
  display: block;
  width: 8px;
  flex-shrink: 0;
  background: var(--wqslide-accent);
}

.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-body {
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-label {
  display: inline-flex;
  align-self: flex-start;
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 700;
  color: #FFFFFF;
  border-radius: 999px;
  background: var(--wqslide-accent);
  margin-bottom: 4px;
}

.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--wqslide-accent-strong);
  line-height: 1.3;
  word-break: keep-all;
  letter-spacing: -0.01em;
}

.wqslide-stage[data-layout="L11-callout"] .wqslide-callout-text {
  font-size: 16px;
  color: var(--wqslide-text-1);
  line-height: 1.55;
  word-break: keep-all;
  margin-top: 4px;
}

/* variant: key — 기본값 (파랑, 위에서 정의됨) */
.wqslide-callout[data-variant="key"] { background: #EFF6FF; border-color: var(--wqslide-accent-soft); }
.wqslide-callout[data-variant="key"] .wqslide-callout-stripe { background: var(--wqslide-accent); }
.wqslide-callout[data-variant="key"] .wqslide-callout-label  { background: var(--wqslide-accent); }
.wqslide-callout[data-variant="key"] .wqslide-callout-title  { color: var(--wqslide-accent-strong); }

/* variant: tip — 청록 */
.wqslide-callout[data-variant="tip"] { background: #F0F9FF; border-color: var(--wqslide-info-soft); }
.wqslide-callout[data-variant="tip"] .wqslide-callout-stripe { background: var(--wqslide-info); }
.wqslide-callout[data-variant="tip"] .wqslide-callout-label  { background: var(--wqslide-info); }
.wqslide-callout[data-variant="tip"] .wqslide-callout-title  { color: var(--wqslide-info-strong); }

/* variant: warn — 노란/주황 */
.wqslide-callout[data-variant="warn"] { background: #FEF9E6; border-color: var(--wqslide-warn-soft); }
.wqslide-callout[data-variant="warn"] .wqslide-callout-stripe { background: var(--wqslide-warn); }
.wqslide-callout[data-variant="warn"] .wqslide-callout-label  { background: var(--wqslide-warn); }
.wqslide-callout[data-variant="warn"] .wqslide-callout-title  { color: var(--wqslide-warn-strong); }

/* variant: correct — 초록 (정답) */
.wqslide-callout[data-variant="correct"] { background: #F0FDF4; border-color: var(--wqslide-success-soft); }
.wqslide-callout[data-variant="correct"] .wqslide-callout-stripe { background: var(--wqslide-success); }
.wqslide-callout[data-variant="correct"] .wqslide-callout-label  { background: var(--wqslide-success); }
.wqslide-callout[data-variant="correct"] .wqslide-callout-title  { color: var(--wqslide-success-strong); }

/* 3개 카드일 때 폰트 축소 */
.wqslide-callout-grid[data-count="3"] .wqslide-callout-title { font-size: 18px; }
.wqslide-callout-grid[data-count="3"] .wqslide-callout-text { font-size: 14px; }
.wqslide-callout-grid[data-count="3"] .wqslide-callout-body { padding: 18px 20px; }

.wqslide-stage[data-layout="L11-callout"][data-variant="stacked_banners"] .wqslide-callout-grid {
  grid-template-columns: 1fr;
  gap: 18px;
}
.wqslide-stage[data-layout="L11-callout"][data-variant="stacked_banners"] .wqslide-callout {
  min-height: 106px;
  display: grid;
  grid-template-columns: 86px 220px 1fr;
  gap: 22px;
  align-items: center;
  padding: 24px 28px;
}
.wqslide-stage[data-layout="L11-callout"][data-variant="stacked_banners"] .wqslide-callout-body {
  display: contents;
}
.wqslide-stage[data-layout="L11-callout"][data-variant="stacked_banners"] .wqslide-callout-title {
  font-size: 26px;
}
.wqslide-stage[data-layout="L11-callout"][data-variant="stacked_banners"] .wqslide-callout-text {
  font-size: 20px;
}

/* ============================================ */
/* L12-closing: 마무리 / 정리                     */
/* ============================================ */
.wqslide-stage[data-layout="L12-closing"] {
  padding: var(--wqslide-padding);
  background: linear-gradient(180deg, #FFFFFF 0%, #EFF6FF 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.wqslide-stage[data-layout="L12-closing"] .wqslide-stage-inner--closing {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.wqslide-stage[data-layout="L12-closing"] .wqslide-closing-eyebrow {
  display: inline-flex;
  align-self: flex-start;
  padding: 6px 18px;
  background: var(--wqslide-accent);
  color: #FFFFFF;
  border-radius: var(--wqslide-radius-pill);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.wqslide-stage[data-layout="L12-closing"] .wqslide-closing-title {
  font-size: 64px;
  font-weight: 800;
  color: var(--wqslide-text-1);
  line-height: 1.2;
  word-break: keep-all;
  letter-spacing: -0.025em;
}
.wqslide-stage[data-layout="L12-closing"] .wqslide-closing-bullets {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
}
.wqslide-stage[data-layout="L12-closing"] .wqslide-closing-bullets li {
  font-size: 28px;
  color: var(--wqslide-text-1);
  font-weight: 600;
  line-height: 1.5;
  padding-left: 36px;
  position: relative;
  word-break: keep-all;
}
.wqslide-stage[data-layout="L12-closing"] .wqslide-closing-bullets li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 16px;
  width: 16px;
  height: 4px;
  border-radius: 2px;
  background: var(--wqslide-accent);
}
.wqslide-stage[data-layout="L12-closing"] .wqslide-closing-footer {
  position: absolute;
  bottom: 60px;
  left: var(--wqslide-padding);
  font-size: 18px;
  color: var(--wqslide-text-3);
  font-weight: 500;
  letter-spacing: 0.04em;
}

.wqslide-stage[data-layout="L12-closing"][data-variant="closing_cards"] .wqslide-closing-bullets {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.wqslide-stage[data-layout="L12-closing"][data-variant="closing_cards"] .wqslide-closing-bullets li {
  min-height: 96px;
  padding: 22px 24px 22px 58px;
  background: var(--wqslide-bg-subtle);
  border-radius: var(--wqslide-radius);
}
.wqslide-stage[data-layout="L12-closing"][data-variant="closing_cards"] .wqslide-closing-bullets li::before {
  left: 24px;
  top: 30px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--wqslide-success);
}

/* ============================================ */
/* 공통: 페이지 번호 (L04/L06)                     */
/* ============================================ */
.wqslide-page-no {
  position: absolute;
  right: 48px;
  bottom: 36px;
  font-size: 18px;
  color: var(--wqslide-text-3);
  font-weight: 600;
  letter-spacing: 0.04em;
}

/* ============================================ */
/* Fallback: 다른 layout (L02/L05/L07/L08/L10/L11, 다음 PR에서 풀 구현) */
/* ============================================ */
.wqslide-fallback {
  padding: var(--wqslide-padding);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 28px;
  background: var(--wqslide-bg);
}

.wqslide-fallback-badge {
  display: inline-flex;
  align-self: flex-start;
  padding: 6px 14px;
  background: var(--wqslide-bg-subtle);
  color: var(--wqslide-text-3);
  border-radius: var(--wqslide-radius-pill);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
}

.wqslide-fallback-title {
  font-size: 56px;
  font-weight: 700;
  color: var(--wqslide-text-1);
  line-height: 1.25;
  word-break: keep-all;
}

.wqslide-fallback-bullets {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.wqslide-fallback-bullets li {
  font-size: var(--wqslide-fs-body);
  color: var(--wqslide-text-2);
  font-weight: 500;
  line-height: 1.5;
  padding-left: 28px;
  position: relative;
}

.wqslide-fallback-bullets li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--wqslide-accent);
}
</style>
</head>
<body>
  <div class="wqslide-stage" data-layout="${layoutId}" data-title-len="${tLen}" data-variant="${escapeHtml(variant)}" data-has-context="${visualLabel ? 'true' : 'false'}">
    ${visualLabel ? `<div class="wqslide-context-label">${escapeHtml(visualLabel)}</div>` : ''}
    ${inner}
  </div>
  ${FONT_GATE_SCRIPT}
</body>
</html>`;
};

const HtmlSlideRenderer = ({ slide, className, forceScale = null, themeId = null }) => {
  const containerRef = useRef(null);
  // 측정 전 scale=0: 1280px 풀사이즈 iframe이 작은 컨테이너에 그대로 그려져
  // "왼쪽 위만 확대된 깨진 미리보기"로 보이는 첫 페인트 문제 방지 (측정 후 표시)
  const [scale, setScale] = useState(forceScale != null ? forceScale : 0);

  // PPT는 1280x720 가로지만 학습지는 A4 세로다. 캔버스가 알려주는 크기를 따른다.
  const pageWidth = (slide && slide.canvas && slide.canvas.width) || SLIDE_W;
  const pageHeight = (slide && slide.canvas && slide.canvas.height) || SLIDE_H;

  // Phase 1: themeId prop이 있으면 그 테마, 없으면 slide.canvas_theme, 없으면 DEFAULT
  // slide.canvas_theme은 outline_to_canvas에서 자동 setting (영속화)
  // (slide.theme은 이미지 PPT용 다른 시스템 — 분리)
  const theme = useMemo(() => {
    const tid = themeId || (slide && slide.canvas_theme) || DEFAULT_THEME_ID;
    return getTheme(tid);
  }, [themeId, slide]);

  const html = useMemo(() => buildSlideHtml(slide || {}, theme), [slide, theme]);

  useEffect(() => {
    if (forceScale != null) return; // 부모가 직접 scale 통제
    if (!containerRef.current) return;
    const el = containerRef.current;
    let disposed = false;
    const update = (width) => {
      if (disposed || !width || width <= 0) return;
      setScale(width / pageWidth);
    };
    // 즉시 측정 시도
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) update(rect.width);
    // ResizeObserver
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      update(w);
    });
    observer.observe(el);
    // 안전망 1: 첫 frame에 한 번 더 측정 (모달 등에서 width가 다음 cycle에 반영되는 경우)
    const rafId = requestAnimationFrame(() => {
      const w2 = el.getBoundingClientRect().width;
      if (w2 > 0) update(w2);
    });
    // 안전망 2: 모달 transition/지연 마운트로 수백 ms 뒤에야 width가 잡히는 케이스
    // (ResizeObserver가 display:none 부모에서는 발화하지 않는 브라우저 대응)
    const timerIds = [250, 800, 2000].map((ms) => setTimeout(() => {
      const w3 = el.getBoundingClientRect().width;
      if (w3 > 0) update(w3);
    }, ms));
    // 안전망 3: 화면에 들어오는 순간 재측정 (collapsed → 펼침, 탭 전환 등)
    let io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          const w4 = el.getBoundingClientRect().width;
          if (w4 > 0) update(w4);
        }
      });
      io.observe(el);
    }
    return () => {
      disposed = true;
      observer.disconnect();
      cancelAnimationFrame(rafId);
      timerIds.forEach(clearTimeout);
      if (io) io.disconnect();
    };
  }, [forceScale, pageWidth]);

  if (!slide) return null;

  const effectiveScale = forceScale != null ? forceScale : scale;
  const isMeasured = effectiveScale > 0;

  return (
    <div
      ref={containerRef}
      className={`wqslide-renderer ${className || ''}`}
      style={{ aspectRatio: `${pageWidth} / ${pageHeight}` }}
    >
      <iframe
        title={`wqslide-${slide.slide_number || 0}`}
        srcDoc={html}
        // allow-scripts: Chart.js (L10-chart) CDN 실행에 필요
        // allow-same-origin 제외: 보안상 부모 페이지의 cookies/localStorage 접근 차단
        sandbox="allow-scripts"
        scrolling="no"
        className="wqslide-renderer-frame"
        style={{
          width: pageWidth,
          height: pageHeight,
          // 측정 전에는 숨겨서 깨진 풀사이즈 페인트 방지 (scale 0 transform 회피)
          transform: `scale(${isMeasured ? effectiveScale : 1})`,
          visibility: isMeasured ? 'visible' : 'hidden',
        }}
      />
    </div>
  );
};

export default HtmlSlideRenderer;
